import { useSnapshot } from 'valtio';

import { AuthState } from '../lib/types';
import { authState } from '../state';
import { useEffect } from 'react';
import AuthModule from '..';
import { PROACTIVE_REFRESH_INTERVAL_MS } from '../constants/auth';

let mockedAuth: AuthState | null = null;

export function setMockedAuth(auth: Partial<AuthState>) {
  mockedAuth = { ...authState, ...auth };
}

/**
 * Hook for accessing authentication state with optional proactive token refresh.
 *
 * @param alwaysFresh - When true, ensures token freshness by checking every 60 seconds.
 *                      Default: false (reactive refresh via Apollo Client is sufficient for most cases)
 *
 * ## Reactive Refresh (Default: alwaysFresh = false)
 * - Token refresh happens automatically before each GraphQL request
 * - Apollo Client's authLink calls getCurrentToken() which refreshes if needed
 * - Sufficient for 99% of use cases
 * - IUM outage resilient: Only calls IUM when token expires within 100 seconds
 *
 * ## Proactive Refresh (alwaysFresh = true)
 * Use ONLY for security-sensitive scenarios where token must stay fresh:
 * - Recording/compliance pages (audit logs, recording exclusions)
 * - Payment processing or PCI-compliant data entry
 * - Long-running operations without API calls (user idle on page >55 min)
 * - Pages with infrequent GraphQL activity (settings pages)
 * - WebSocket-only pages requiring token for non-GraphQL operations
 *
 * ## IUM Outage Resilience
 * - During token validity window (>100s remaining): NO IUM calls, unaffected by outages
 * - Within 100s of expiry: Attempts refresh, falls back to valid token if IUM down
 * - After expiry: Refresh required, forces re-authentication if IUM unavailable
 *
 * @returns Authentication state snapshot (signedIn, user, loading, token, etc.)
 *
 * @example
 * // Most components (reactive refresh via GraphQL)
 * const { user, token } = useAuth();
 *
 * @example
 * // Security-sensitive page (proactive refresh every 60s)
 * const { user, token } = useAuth(true);
 */
function useAuth(alwaysFresh = false) {
  const auth = useSnapshot(authState);

  useEffect(() => {
    if (alwaysFresh) {
      // Proactive token refresh: Check token validity every 60 seconds
      // getCurrentToken() only calls IUM if token expires within 100 seconds
      // Otherwise uses cached token (IUM outage resilient)
      const interval = setInterval(() => {
        AuthModule.getCurrentToken();
      }, PROACTIVE_REFRESH_INTERVAL_MS);

      return () => clearInterval(interval);
    }
  }, [alwaysFresh]);

  if (mockedAuth) {
    return mockedAuth;
  }

  return auth;
}

export default useAuth;
